import { useState, useEffect } from "react";
import {
  MenuItem,
  Select,
  FormControl,
  Autocomplete,
  TextField,
  IconButton,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { IoIosArrowBack } from "react-icons/io";
import { useForm, Controller } from "react-hook-form";
import { fetchAllEntities } from "../../apis/apiService";
import {
  useFetchGetallDepartmentsQuery,
  useFetchGetallUserListQuery,
} from "../../slice/logdetailSlice";
import "../../App.css";

export const FilterCertificate = ({ onApply, onCancel, currentFilters, login_user_role, rawCertificates }) => {
  const [awardOptions, setAwardOptions] = useState([]);
  const [quarterOptions, setQuarterOptions] = useState([]);
  const [loadingAwards, setLoadingAwards] = useState(false);
  const [loadingPeriods, setLoadingPeriods] = useState(false);

  const { data: allUsers } = useFetchGetallUserListQuery();
  const { data: departmentList = [] } = useFetchGetallDepartmentsQuery();

  const { handleSubmit, control, reset } = useForm({
    defaultValues: {
      employee_id: Array.isArray(currentFilters?.employee_id)
        ? currentFilters.employee_id
        : (currentFilters?.employee_id && currentFilters.employee_id !== "all"
          ? [currentFilters.employee_id]
          : []),
      department_id: Array.isArray(currentFilters?.department_id)
        ? (currentFilters.department_id[0] ?? "")
        : (currentFilters?.department_id || ""),
      period_id: Array.isArray(currentFilters?.period_id)
        ? (currentFilters.period_id[0] ?? "")
        : (currentFilters?.period_id || ""),
      award_id: Array.isArray(currentFilters?.award_id)
        ? (currentFilters.award_id[0] ?? "")
        : (currentFilters?.award_id || ""),
    },
  });

  // Fetch Quarter options
  useEffect(() => {
    const fetchAwardPeriods = async () => {
      setLoadingPeriods(true);
      try {
        const response = await fetchAllEntities("master/award-periods/show");
        if (response && response.data) {
          const periodsArray = Array.isArray(response.data)
            ? response.data
            : Object.values(response.data);
          
          const normalized = periodsArray.map(p => ({
            ...p,
            period_id: p.period_id || p.id || p.award_period_id,
            period_label: p.period_label || p.label
          })).sort((a, b) => (a.period_label || a.label).localeCompare(b.period_label || b.label));
          setQuarterOptions(normalized);
        }
      } catch (err) {
        console.error("Error fetching award periods:", err);
      } finally {
        setLoadingPeriods(false);
      }
    };
    fetchAwardPeriods();
  }, []);

  // Fetch Award Name options
  useEffect(() => {
    const fetchAwards = async () => {
      setLoadingAwards(true);
      try {
        const response = await fetchAllEntities("master/awards/show");
        if (response && response.data) {
          const awardsArray = Array.isArray(response.data)
            ? response.data
            : Object.values(response.data);
          
          if (awardsArray.length > 0) {
            const normalized = awardsArray.map(a => ({
              ...a,
              award_id: a.award_id || a.id,
              award_name: a.award_name || a.name
            }));
            setAwardOptions(normalized);
          }
        }
      } catch (err) {
        console.error("Error fetching awards:", err);
      } finally {
        setLoadingAwards(false);
      }
    };
    fetchAwards();
  }, []);

  // Fallback for Quarters and Awards if master API returns empty
  useEffect(() => {
    if ((quarterOptions.length === 0 || awardOptions.length === 0) && rawCertificates) {
      const certificatesArray = Array.isArray(rawCertificates) ? rawCertificates : Object.values(rawCertificates);
      const extractedQuarters = new Map();
      const extractedAwards = new Map();

      certificatesArray.forEach(award => {
        // Extract Award
        const aid = String(award.award_id || award.id);
        if (aid && !extractedAwards.has(aid) && awardOptions.length === 0) {
          extractedAwards.set(aid, {
            award_id: aid,
            award_name: award.award_name
          });
        }

        // Extract Quarters
        if (award.periods && Array.isArray(award.periods) && quarterOptions.length === 0) {
          award.periods.forEach(p => {
            const pid = String(p.period_id || p.id);
            if (pid && !extractedQuarters.has(pid)) {
              extractedQuarters.set(pid, {
                period_id: pid,
                period_label: p.period_label || p.label
              });
            }
          });
        }
      });

      if (extractedQuarters.size > 0 && quarterOptions.length === 0) {
        setQuarterOptions(Array.from(extractedQuarters.values()).sort((a, b) => a.period_label.localeCompare(b.period_label)));
      }
      if (extractedAwards.size > 0 && awardOptions.length === 0) {
        setAwardOptions(Array.from(extractedAwards.values()).sort((a, b) => a.award_name.localeCompare(b.award_name)));
      }
    }
  }, [rawCertificates, quarterOptions.length, awardOptions.length]);

  const onSubmit = (data) => {
    onApply({
      ...data,
      employee_id: Array.isArray(data.employee_id) ? data.employee_id : [],
      department_id: data.department_id ? [data.department_id] : [],
      period_id: data.period_id ? [data.period_id] : [],
      award_id: data.award_id ? [data.award_id] : [],
    });
  };

  const handleClear = () => {
    const clearedValues = {
      employee_id: [],
      department_id: "",
      period_id: "",
      award_id: "",
    };
    reset(clearedValues);
    onApply({
      ...clearedValues,
      department_id: [],
      period_id: [],
      award_id: [],
    });
  };

  const useTheme = () => {
    const [isDarkMode, setIsDarkMode] = useState(false);

    useEffect(() => {
      const checkTheme = () => {
        const dark =
          document.body.classList.contains("bg-dark") ||
          document.body.classList.contains("dark-mode") ||
          document.documentElement.getAttribute("data-theme") === "dark";
        setIsDarkMode(dark);
      };

      checkTheme();

      const observer = new MutationObserver(checkTheme);
      observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

      return () => observer.disconnect();
    }, []);

    return isDarkMode;
  };

  const isDarkMode = useTheme();
  const selectedTextColor = isDarkMode ? "#ffffff" : "#000000";
  const placeholderColor = "#999999";

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div className="filter-h-bg px-4 py-3 d-flex justify-content-between">
        <button type="button" onClick={onCancel} className="R_btn">
          <div className="d-flex text-rgba-gray align-items-center form-label">
            <IoIosArrowBack className="me-1" /> Filter
          </div>
        </button>
        <button type="button" onClick={handleClear} className="R_btn tex-tgba-red clear-filter-btn">
          Clear Filter
        </button>
      </div>

      <div className="bg-white px-4 py-3" style={{
        flex: 1,
        overflowY: 'auto',
        maxHeight: 'calc(100vh - 140px)'
      }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="row">
            <div className="col-12">
              <div className="row">
                {/* Users Filter - Hidden for Employees */}
                {login_user_role !== "EMPLOYEE" && (
                  <div className="col-12 mb-4">
                    <label className="form-label mb-2">Employee Name</label>
                    <Controller
                      name="employee_id"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          multiple
                          options={allUsers || []}
                          getOptionLabel={(option) => option.name || ""}
                          disableCloseOnSelect
                          value={
                            (allUsers || []).filter(
                              (u) => Array.isArray(field.value) && field.value.some((id) => String(id) === String(u.user_id))
                            )
                          }
                          onChange={(e, newValue) => {
                            field.onChange(newValue ? newValue.map((value) => value.user_id) : []);
                          }}
                          filterOptions={(options, { inputValue }) => {
                            return options.filter((option) =>
                              option.name?.toLowerCase().includes(inputValue?.toLowerCase() || "")
                            );
                          }}
                          renderOption={(props, option, { selected }) => {
                            const { key, ...optionProps } = props;
                            return (
                              <li key={key} {...optionProps}>
                                <Checkbox
                                  style={{ marginRight: 8 }}
                                  checked={selected}
                                />
                                {option.name}
                              </li>
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={Array.isArray(field.value) && field.value.length > 0 ? "" : "Select Employees"}
                              sx={{
                                "& .MuiInputBase-input::placeholder": { color: placeholderColor },
                                "& .MuiInputBase-input": {
                                  color: Array.isArray(field.value) && field.value.length > 0 ? selectedTextColor : placeholderColor
                                },
                              }}
                            />
                          )}
                        />
                      )}
                    />
                  </div>
                )}

                {/* Department Filter - Hidden for Employees */}
                {login_user_role !== "EMPLOYEE" && (
                  <div className="col-12 mb-4">
                    <label className="form-label mb-2">Department</label>
                    <Controller
                      name="department_id"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <Select
                            {...field}
                            displayEmpty
                            value={field.value || ""}
                            sx={{
                              '& .clear-icon': { opacity: 0, transition: '0.2s' },
                              '&:hover .clear-icon': { opacity: 1 }
                            }}
                            endAdornment={
                              field.value ? (
                                <IconButton
                                  className="clear-icon text-rgbr-red"
                                  size="small"
                                  sx={{
                                    position: 'absolute',
                                    right: 28,
                                    '&:hover': { color: 'error.main' }
                                  }}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={() => field.onChange("")}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              ) : null
                            }
                            renderValue={(selected) => {
                              if (!selected) {
                                return <span style={{ color: "#999999" }}>Select Department</span>;
                              }
                              return (
                                <span style={{ color: selectedTextColor }}>
                                  {departmentList.find((d) => String(d.department_id) === String(selected))?.department_name || ""}
                                </span>
                              );
                            }}
                          >
                            {departmentList.map((dept) => (
                              <MenuItem key={dept.department_id} value={dept.department_id}>
                                {dept.department_name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </div>
                )}

                {/* Period (Quarter) Filter */}
                <div className="col-6 mb-4">
                  <label className="form-label mb-2">Quarter Name</label>
                  <Controller
                    name="period_id"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <Select
                          {...field}
                          displayEmpty
                          value={field.value || ""}
                          sx={{
                            '& .clear-icon': { opacity: 0, transition: '0.2s' },
                            '&:hover .clear-icon': { opacity: 1 }
                          }}
                          endAdornment={
                            field.value ? (
                              <IconButton
                                className="clear-icon text-rgbr-red"
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  right: 28,
                                  '&:hover': { color: 'error.main' }
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={() => field.onChange("")}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            ) : null
                          }
                          renderValue={(selected) => {
                            if (!selected) {
                              return <span style={{ color: "#999999" }}>Select Quarter</span>;
                            }
                            return (
                              <span style={{ color: selectedTextColor }}>
                                {quarterOptions.find((opt) => String(opt.period_id) === String(selected))?.period_label || ""}
                              </span>
                            );
                          }}
                        >
                          {quarterOptions.map((q) => (
                            <MenuItem key={q.period_id || q.id} value={String(q.period_id || q.id)}>
                              {q.period_label || q.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </div>

                {/* Award Filter */}
                <div className="col-6 mb-4">
                  <label className="form-label mb-2">Award Name</label>
                  <Controller
                    name="award_id"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <Select
                          {...field}
                          displayEmpty
                          value={field.value || ""}
                          sx={{
                            '& .clear-icon': { opacity: 0, transition: '0.2s' },
                            '&:hover .clear-icon': { opacity: 1 }
                          }}
                          endAdornment={
                            field.value ? (
                              <IconButton
                                className="clear-icon text-rgbr-red"
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  right: 28,
                                  '&:hover': { color: 'error.main' }
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={() => field.onChange("")}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            ) : null
                          }
                          renderValue={(selected) => {
                            if (!selected) {
                              return <span style={{ color: "#999999" }}>Select Award</span>;
                            }
                            return (
                              <span style={{ color: selectedTextColor }}>
                                {awardOptions.find((opt) => String(opt.id || opt.award_id) === String(selected))?.award_name || ""}
                              </span>
                            );
                          }}
                        >
                          {awardOptions.map((a) => (
                            <MenuItem key={a.award_id || a.id} value={String(a.award_id || a.id)}>
                              {a.award_name || a.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Buttons fixed at bottom */}
      <div className="px-4 py-3 bg-white">
        <div className="d-flex justify-content-start">
          <button
            type="button"
            className="add_btn me-3"
            onClick={handleSubmit(onSubmit)}
          >
            Apply
          </button>
          <button
            type="button"
            className="cancel_btn"
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
